import type { MPAInterceptedResponse } from '@repo/api-gw-sdk';
import type { ReactNode } from 'react';

export const isMPAInterceptedResponse = (
  body: unknown
): body is MPAInterceptedResponse => {
  if (!body || typeof body !== 'object') {
    return false;
  }
  const candidate = body as Record<string, unknown>;
  return (
    typeof candidate.actionApprovalRequest === 'object' &&
    candidate.actionApprovalRequest !== null &&
    typeof candidate.mpaPolicies === 'object' &&
    candidate.mpaPolicies !== null
  );
};

// MPA-intercepted responses are absorbed by the SDK middleware before the
// caller awaits, so the runtime value is either the success type or undefined
// (when the original op was deferred for approval). Use this at the DAL
// boundary to drop the MPA branch from the codegen union.
export const stripMpa = <T>(
  p: Promise<MPAInterceptedResponse | T>
): Promise<T | undefined> =>
  p.then((res) => (isMPAInterceptedResponse(res) ? undefined : res));

// The Data Platform console is a federated remote with its own copy of this
// module, so `instanceof` fails against errors the host throws. Recognise the
// MPA outcomes by `name`, which survives the bundle boundary. The remote loads
// `latest` and runs ahead of the deployed host (src/libs/mfe/AGENTS.md), so
// never rename these in place: accept the old and new name until the host that
// throws the new one is in production.
const MPA_CANCELLED = 'MpaCancelledError';
const MPA_APPROVAL_REQUESTED = 'MpaApprovalRequestedError';

export class MpaCancelledError extends Error {
  constructor() {
    super('Operation cancelled by user');
    Object.setPrototypeOf(this, MpaCancelledError.prototype);
    this.name = MPA_CANCELLED;
  }
}

// Thrown after the user submits an MPA approval request from the intercept
// dialog. The original action did NOT execute — only an approval request was
// created. Callers should treat this as a non-error completion and surface
// "Approval request sent" feedback rather than the original action's snackbar.
export class MpaApprovalRequestedError extends Error {
  constructor() {
    super('Approval request sent');
    Object.setPrototypeOf(this, MpaApprovalRequestedError.prototype);
    this.name = MPA_APPROVAL_REQUESTED;
  }
}

export const isMpaCancelled = (error: unknown): boolean =>
  error instanceof Error && error.name === MPA_CANCELLED;

export const isMpaApprovalRequested = (error: unknown): boolean =>
  error instanceof Error && error.name === MPA_APPROVAL_REQUESTED;

// Common handler for MPA-intercept errors thrown from a user action.
// Returns `true` if the error was an MPA cancel/approval (caller should stop
// further error handling); returns `false` otherwise so the caller can fall
// through to its existing error path. On approval, surfaces the standard
// "Approval request sent successfully" snackbar, or `approvalMessage` when
// given. `abort` is called for both branches and is optional — pass it to
// close the surrounding wizard/dialog, or omit it for fire-and-forget actions
// (e.g. an option-menu click).
export const handleMpaInterceptInWizard = (
  error: unknown,
  abort: (() => void) | undefined,
  snackbar: { showMessage: (msg: ReactNode) => void },
  approvalMessage: ReactNode = 'Approval request sent successfully'
): boolean => {
  if (isMpaCancelled(error)) {
    abort?.();
    return true;
  }
  if (isMpaApprovalRequested(error)) {
    snackbar.showMessage(approvalMessage);
    abort?.();
    return true;
  }
  return false;
};
